// apps/web/app/microgrid/lit/page.tsx
'use client';

import React from 'react';
import LitGlobalStyles from './LitGlobalStyles';
import Nav from './components/Nav';
import Hero from './components/Hero';
import WhyChoose from './components/WhyChoose';
import HowItWorks from './components/HowItWorks';
import PathCTA from './components/PathCTA';
import { useReveal } from '../shared/useReveal';
import SocialBar from './components/SocialBar';

/* ---------- Socials (copied from LitHomeClient, simplified styling) ---------- */
type SocialLink = { href: string; label: string; Icon: React.FC<React.SVGProps<SVGSVGElement>> };

const wrap = { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', marginTop: '1rem' };
const aCls: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0.75rem',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,.15)',
  background: 'rgba(0,0,0,.06)',
  textDecoration: 'none',
  color: 'inherit',
  fontSize: '.9rem',
};
const iconCls: React.CSSProperties = { width: 18, height: 18 };

const TikTokIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 48 48" aria-hidden {...props}><path fill="currentColor" d="M31 6c1 5 4 9 9 10v6c-3 0-6-1-9-3v11c0 8-6 12-12 12S6 38 6 32s4-12 12-12h1v6h-1c-3 0-6 3-6 6s3 6 6 6 6-2 6-6V6h6z"/></svg>
);
const FacebookIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" aria-hidden {...props}><path fill="currentColor" d="M22 12a10 10 0 1 0-11.6 9.9v-7h-2.3V12h2.3V9.8c0-2.2 1.3-3.4 3.2-3.4.9 0 1.9.2 1.9.2v2.1h-1.1c-1 0-1.3.6-1.3 1.2V12h2.5l-.4 2.9h-2.1v7A10 10 0 0 0 22 12"/></svg>
);
const InstagramIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" aria-hidden {...props}>
    <path
      fill="currentColor"
      d="M12 2.163c3.204 0 3.584.012 4.85.07 1.172.053 1.902.218 2.592.465a4.902 4.902 0 0 1 1.757 1.757c.247.69.412 1.42.465 2.592.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.053 1.172-.218 1.902-.465 2.592a4.902 4.902 0 0 1-1.757 1.757c-.69.247-1.42.412-2.592.465-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.172-.053-1.902-.218-2.592-.465a4.902 4.902 0 0 1-1.757-1.757c-.247-.69-.412-1.42-.465-2.592-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.053-1.172.218-1.902.465-2.592A4.902 4.902 0 0 1 4.55 4.477c.69-.247 1.42-.412 2.592.465C8.416 2.175 8.796 2.163 12 2.163zM12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 8c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3zm6.406-9.66c-.78 0-1.418.638-1.418 1.418s.638 1.418 1.418 1.418 1.418-.638 1.418-1.418-.638-1.418-1.418-1.418z"
    />
  </svg>
);
const YouTubeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" aria-hidden {...props}><path fill="currentColor" d="M23.5 6.2c-.3-1.3-1.3-2.3-2.6-2.6C19 3.1 12 3.1 12 3.1s-7 0-8.9.5C1.8 4 0.8 5 0.5 6.3 0 8.2 0 12 0 12s0 3.8.5 5.7c.3 1.3 1.3 2.3 2.6 2.6 1.9.5 8.9.5 8.9.5s7 0 8.9-.5c1.3-.3 2.3-1.3 2.6-2.6.5-1.9.5-5.7.5-5.7s0-3.8-.5-5.7zM9.7 15.6V8.4l6.1 3.6-6.1 3.6z"/></svg>
);
const NextdoorIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" aria-hidden {...props}><path fill="currentColor" d="M20.7 6.5c.6 1 .9 2.2.9 3.5v7.9h-3.3v-7.2c0-1.6-.6-2.4-1.9-2.4-1.2 0-2 .8-2 2.4v7.2H11V6.8h3.1l.1 1.3c.7-.9 1.8-1.5 3.2-1.5 1.3 0 2.4.5 3.3 1.9zM6.3 6.8c1.4 0 2.5.6 3.2 1.5l.1-1.3H13v11H9.7v-7.2c0-1.6-.8-2.4-2-2.4-1.3 0-1.9.8-1.9 2.4v7.2H2.5V10c0-3.2 1.8-5.1 3.8-5.1z"/></svg>
);
const YelpIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" aria-hidden {...props}><path fill="currentColor" d="M10.6 2.1c-.6-.2-1.3.1-1.5.7L7.3 8.3c-.2.6.1 1.3.7 1.5l1.5.5c.6.2 1.3-.1 1.5-.7l1.8-5.5c.2-.6-.1-1.3-.7-1.5l-1.5-.5zM19.7 8.2l-5.5-1.8c-.6-.2-1.3.1-1.5.7l-.5 1.5c-.2.6.1 1.3.7 1.5l5.5 1.8c.6.2 1.3-.1 1.5-.7l.5-1.5c.2-.6-.1-1.3-.7-1.5zM8.9 12.6l-5.5.9c-.6.1-1 .7-.9 1.3l.3 1.5c.1.6.7 1 1.3.9l5.5-.9c.6-.1 1-.7.9-1.3l-.3-1.5c-.1-.6-.7-1-1.3-.9zM13.2 20.7l1.8-5.5c.2-.6-.1-1.3-.7-1.5l-1.5-.5c-.6-.2-1.3.1-1.5.7l-1.8 5.5c-.2.6.1 1.3.7 1.5l1.5.5c.6.2 1.3-.1 1.5-.7zM21.7 14.3l-5.5.9c-.6.1-1 .7-.9 1.3l.3 1.5c.1.6.7 1 1.3.9l5.5-.9c.6-.1 1-.7.9-1.3l-.3-1.5c-.1-.6-.7-1-1.3-.9z"/></svg>
);

const SOCIALS: SocialLink[] = [
  { href: 'https://www.tiktok.com/@always.lit6', label: 'TikTok', Icon: TikTokIcon },
  { href: 'https://www.facebook.com/alwayslittx', label: 'Facebook', Icon: FacebookIcon },
  { href: 'https://www.instagram.com/always_lit_tx/', label: 'Instagram', Icon: InstagramIcon },
  { href: 'https://www.youtube.com/@alwayslit-tx', label: 'YouTube', Icon: YouTubeIcon },
  { href: 'https://nextdoor.com/pages/always-lit-llc-san-antonio-tx', label: 'Nextdoor', Icon: NextdoorIcon },
  {
    href: 'https://www.yelp.com/writeareview/biz/K7O4iHy8qZGTp0psWMIoOw?return_url=%2Fbiz%2FK7O4iHy8qZGTp0psWMIoOw&review_origin=biz-details-war-button',
    label: 'Yelp',
    Icon: YelpIcon,
  },
];
/* --------------------------------------------------------------------------- */

const Page: React.FC = () => {
  useReveal(['.feature-card', '.step-card']); // reveal on scroll

  return (
    <>
      <LitGlobalStyles />
      <Nav />
      <main className="landing">
        {/* Only hero is visible during loading; other sections are hidden by CSS until body.loaded */}
        <Hero />
        <WhyChoose />
        <HowItWorks />
        <PathCTA />
      </main>
      <footer style={{ textAlign: 'center', padding: '2rem 1rem' }}>
        <p>&copy; 2025 Always Lit Homes. All rights reserved.</p>
        <p>Our office is located at 311 West Nakoma St, San Antonio, TX, 78216</p>
        <p>
          (210) 429-9018 | <a href="mailto:info@alwayslittx.com">info@alwayslittx.com</a>
        </p>
        {/* Socials at the very end */}
        <SocialBar />
      </footer>
    </>
  );
};

export default Page;
